import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, CalendarCheck, Download, ClipboardCheck, MessageSquare, X, AlertTriangle } from 'lucide-react';
import { BulkAttendeePicker } from './BulkAttendeePicker';
import { EvaluateDialog } from './EvaluateDialog';

export default function TrainingSessionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [sessions, setSessions] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [emps, setEmps] = useState([]);
  const [fps, setFps] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [feedbackDlg, setFeedbackDlg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, t, e, f] = await Promise.all([hrAPI.listSessions(), hrAPI.listTrainings(), hrAPI.listEmployees(), hrAPI.listFunctionalPositions()]);
      setSessions(s.data || []); setTrainings(t.data || []); setEmps(e.data || []); setFps(f.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const myEmpId = emps.find(e => e.user_id === user?.id)?.id;

  const openNew = () => setDlg({ training_id: '', trainer_id: '', planned_date: '', actual_date: '', attendee_ids: [] });
  const toggleAtt = (id) => setDlg(d => ({ ...d, attendee_ids: d.attendee_ids.includes(id) ? d.attendee_ids.filter(x => x !== id) : [...d.attendee_ids, id] }));

  const save = async () => {
    if (!dlg.training_id) { toast.error('Select a training'); return; }
    if (dlg.attendee_ids.length === 0) { toast.error('Add at least one attendee'); return; }
    setSaving(true);
    try {
      const payload = { ...dlg, is_group: dlg.attendee_ids.length > 1 };
      if (dlg.id) await hrAPI.updateSession(dlg.id, payload); else await hrAPI.createSession(payload);
      toast.success('Session saved'); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const complete = async (s) => {
    const d = window.prompt('Enter actual training date (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
    if (!d) return;
    try { const r = await hrAPI.completeSession(s.id, { actual_date: d }); toast.success(r.data.skill_update_required ? `Completed. Skill update due ${r.data.due_date}` : 'Completed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startTest = (s) => {
    const lt = s.linked_tests || [];
    if (lt.length === 0) { toast.error('No competency test is linked to this training yet. Create one under Competency Tests.'); return; }
    navigate(`/hr/tests/${lt[0].id}/take`);
  };

  const downloadRecord = async (s) => {
    try {
      const res = await hrAPI.sessionRecord(s.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `Training_Record_F-HR-05_${s.id.slice(0, 8)}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to download F/HR/05'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-sessions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><CalendarCheck className="w-6 h-6 text-pink-600" /> Training Sessions</h1>
          <p className="text-sm text-slate-500 mt-0.5">Schedule, conduct and record trainings; attendees take the online test</p>
        </div>
        {canManage && <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={openNew} data-testid="hr-session-add"><Plus className="w-4 h-4" /> Schedule Training</Button>}
      </div>

      <div className="space-y-3">
        {sessions.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No training sessions yet</p>}
        {sessions.map(s => {
          const iAmAttendee = myEmpId && s.attendees.some(a => a.employee_id === myEmpId);
          return (
            <Card key={s.id} className="border border-slate-200 shadow-none" data-testid={`hr-session-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{s.training_title} {s.is_group && <Badge variant="outline" className="text-[10px] ml-1">Group</Badge>}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Trainer: {s.trainer_name || '—'} · Planned: {s.planned_date || '—'} · Actual: {s.actual_date || '—'}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {s.attendees.map(a => <Badge key={a.employee_id} variant="outline" className={`text-[10px] ${s.status === 'completed' && !a.evaluated ? 'bg-red-50 text-red-700 border-red-200' : ''}`}>{a.employee_name}{a.test_score != null ? ` · ${a.test_score}/${a.test_total}` : ''}{a.skill_update_status === 'pending' ? ' · update pending' : ''}{s.status === 'completed' && !a.evaluated ? ' · eval pending' : ''}</Badge>)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className={`text-[10px] ${s.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{s.status}</Badge>
                    {s.eval_pending && <Badge variant="outline" data-testid={`hr-session-eval-pending-${s.id}`} className="text-[10px] bg-red-50 text-red-700 border-red-200 gap-1"><AlertTriangle className="w-2.5 h-2.5" />Evaluation Pending</Badge>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {canManage && s.status !== 'completed' && <Button size="sm" variant="outline" onClick={() => complete(s)} className="gap-1" data-testid={`hr-session-complete-${s.id}`}><ClipboardCheck className="w-3.5 h-3.5" /> Mark Complete</Button>}
                  {iAmAttendee && <Button size="sm" variant="outline" onClick={() => startTest(s)} className="gap-1" data-testid={`hr-session-test-${s.id}`}>Take Test</Button>}
                  {canManage && s.status === 'completed' && s.attendees.map(a => <Button key={a.employee_id} size="sm" variant="ghost" className={`gap-1 text-xs ${!a.evaluated ? 'text-red-600 hover:text-red-700' : 'text-emerald-600'}`} onClick={() => setFeedbackDlg({ sid: s.id, empId: a.employee_id, name: a.employee_name, supervisor_comment: a.supervisor_comment || '', result: a.result || '', update_immediately: a.update_immediately, objective_evidence: a.objective_evidence || '', certificates: a.certificates || '', correction_plan: a.correction_plan || '', attachments: a.attachments || [] })} data-testid={`hr-session-feedback-${s.id}-${a.employee_id}`}><MessageSquare className="w-3.5 h-3.5" /> {a.evaluated ? 'Edit eval' : 'Evaluate'}: {a.employee_name}</Button>)}
                  <Button size="sm" variant="ghost" className="gap-1" onClick={() => downloadRecord(s)} data-testid={`hr-session-record-${s.id}`}><Download className="w-3.5 h-3.5" /> F/HR/05</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Schedule dialog */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hr-session-dialog">
          <DialogHeader><DialogTitle>Schedule Training</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Training *</Label>
                <Select value={dlg.training_id || undefined} onValueChange={v => setDlg({ ...dlg, training_id: v })}>
                  <SelectTrigger data-testid="hr-session-training"><SelectValue placeholder="Select training" /></SelectTrigger>
                  <SelectContent>{trainings.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Trainer</Label>
                <Select value={dlg.trainer_id || '__none__'} onValueChange={v => setDlg({ ...dlg, trainer_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="hr-session-trainer"><SelectValue placeholder="Trainer" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">None</SelectItem>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Planned Date</Label><Input type="date" value={dlg.planned_date} onChange={e => setDlg({ ...dlg, planned_date: e.target.value })} data-testid="hr-session-planned" /></div>
                <div><Label className="text-xs">Actual Date</Label><Input type="date" value={dlg.actual_date} onChange={e => setDlg({ ...dlg, actual_date: e.target.value })} /></div>
              </div>
              <div>
                <Label className="text-xs">Attendees ({dlg.attendee_ids.length}) — multiple = Group</Label>
                <div className="mt-1">
                  <BulkAttendeePicker emps={emps} fps={fps} selectedIds={dlg.attendee_ids} onChange={(ids) => setDlg(d => ({ ...d, attendee_ids: ids }))} testidPrefix="hr-session-att" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-session-save">{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor evaluation dialog (shared) */}
      <EvaluateDialog data={feedbackDlg} onClose={() => setFeedbackDlg(null)} onSaved={load} />
    </div>
  );
}
